import React, { useEffect, useState } from 'react';
import { Col, Dropdown, Menu, Modal, Row, Spin } from 'antd';
import SideNav from '../company-settings/side-nav/component';
import { axiosInstanceB2B, formatAmount, Toaster } from '@share/utils';
import { useDispatch, useSelector } from 'react-redux';
import { ILoginState } from '@share/store/slices';
import './style.scss';
import { Loading, SearchTable, WhiteButton } from '@components';
import { FILE_TYPE, MEMBER_STATUS, useMyCreditLineColumnMenu } from '@constants';
import { CreditLine } from '../../../common-types/credit-line';
import { BlueButton } from '@share/components';
import { FormattedMessage, useIntl } from 'react-intl';
import CompanyPlaidComponent from '../company-plaid-service/component';
import {
  addNewBankName,
  removeSelectedCreditLineData,
  savePayment,
  setCreditLineData,
  setCreditLineSummary,
  setDueAmount,
} from '@store/slices';
import { Urls } from '@share/constants';
import CompanyCreditLinePaymentsList from '../company-credit-line-payments-list/component';
import { LoadingSise } from '@common-types';
import { Form } from 'react-bootstrap/lib/Navbar';

interface IMapStateToProps {
  loginStore: ILoginState;
}

function CompanyCreditLineComponent() {
  const login = useSelector((state: IMapStateToProps) => state.loginStore);
  const paymentList = useSelector((state: any) => state?.creditLineStore?.payments);
  const intl = useIntl();
  const selectedCreditLine = useSelector(
    (state: any) => state?.creditLineStore?.selectedCreditLine,
  );
  const dueAmount = (
    parseFloat(useSelector((state: any) => state?.creditLineStore?.dueAmount)) || 0
  ).toFixed(2);
  const totalPending = useSelector((state: any) => state?.creditLineStore?.totalPending);
  const dueDate = useSelector((state: any) => state?.creditLineStore?.dueDate);
  const dispatch = useDispatch();
  const [creditLine, setCreditLine] = useState<CreditLine | null>(null);
  const creditLineInvoices = useSelector((state: any) => state?.creditLineStore?.creditLine);
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isProceedModalVisible, setIsProceedModalVisible] = useState<boolean>(false);
  const [isBankloading, setIsBankLoading] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [activeTab, setActiveTab] = useState<string>('invoices');
  const [filters, setFilters] = useState({
    bookingInternalId: { value: null },
    bookingStatusEnum: { value: null },
    customerLocator: { value: null },
    paidDate: { value: null },
  });
  const [selectedBank, setSelectedBank] = useState<{
    bankId: number;
    bankName: string;
    lastFourDigits: string;
    bankType: string;
    loading: boolean;
  } | null>(null);
  const [progress, setProgress] = useState(50);
  const [bankListLoading, setBankListLoading] = useState<boolean>(false);
  const [paymentListLoading, setPaymentListLoading] = useState<boolean>(false);
  const [bankName, setBankName] = useState<
    {
      bankId: number;
      bankName: string;
      lastFourDigits: string;
      bankType: string;
    }[]
  >([]);
  const [plaidState, setPlaidState] = useState<{
    isPlaidToken: boolean;
    isLoading: boolean;
    linkToken: string;
    expiration: string;
    requestID: string;
    publicToken: string;
    itemId: string;
  }>({
    isPlaidToken: false,
    isLoading: false,
    linkToken: '',
    expiration: '',
    requestID: '',
    publicToken: '',
    itemId: '',
  });
  const modalWidth = 600;

  const getBankAccountList = async () => {
    try {
      setBankListLoading(true);
      const result = await axiosInstanceB2B.get(
        `/payments/plaid/GetBankAccountList/${login?.account?.id}`,
      );
      const bankList = result?.data?.plaidBankAcountDetailDtoList;
      if (result?.data.plaidBankAcountDetailDtoList) {
        const updatedBankList = bankList?.map((item: any) => ({
          bankId: item?.bankId,
          bankName: item?.bankName,
          lastFourDigits: item?.lastFourDigits,
          bankType: item?.type,
        }));
        dispatch(addNewBankName(updatedBankList));
        bankList.map((item: any) => {
          setBankName((prev) => [
            ...prev,
            {
              bankId: item?.bankId,
              bankName: item?.bankName,
              lastFourDigits: item?.lastFourDigits,
              bankType: item?.type,
            },
          ]);
        });
      }
      setBankListLoading(false);
    } catch (error) {
      setBankListLoading(false);
      console.error('Error fetching credit line data:', error);
    }
  };
  const getPlaidACHPaymentList = async () => {
    try {
      setPaymentListLoading(true);
      const result = await axiosInstanceB2B.get(
        `${Urls.GetPlaidACHPaymentList}/${login?.account?.id}`,
      );
      if (result?.data.payments) {
        const { data } = result;
        dispatch(savePayment(data?.payments));
        setPaymentListLoading(false);
      }
    } catch (error) {
      setPaymentListLoading(false);
      console.error('Error fetching credit line data:', error);
    }
  };

  const handleCreatePlaidToken = async () => {
    try {
      setPlaidState((prevState) => ({ ...prevState, isLoading: true }));
      const result = await axiosInstanceB2B.get('/payments/plaid/CreateLinkToken');
      if (result?.status === 200) {
        setPlaidState({
          isPlaidToken: true,
          isLoading: false,
          linkToken: result?.data?.link_token,
          expiration: result?.data?.expiration,
          requestID: result?.data?.request_id,
          publicToken: '',
          itemId: '',
        });
        setIsModalVisible(true);
      }
    } catch (error) {
      setPlaidState((prevState) => ({ ...prevState, isLoading: false }));
      Toaster.error('An error occurred. Please try again.');
    }
  };

  const handlePlaidSuccess = async (publicToken: string, metadata: any) => {
    if (publicToken) {
      setIsBankLoading(true);
      setIsModalVisible(true);
      try {
        const result = await axiosInstanceB2B.post('/payments/plaid/SaveBankDetail', {
          PublicToken: publicToken,
          AccountId: login?.account?.id,
          BankName: metadata?.institution?.name,
        });
        if (result?.status === 200) {
          setBankName((prev) => [
            ...prev,
            {
              bankId: result?.data?.BankDetailId,
              bankName: metadata?.institution?.name || '',
              lastFourDigits: metadata?.account?.mask || '',
              bankType: metadata?.account?.type || 'Saving',
            },
          ]);
          setPlaidState((prevState) => ({
            ...prevState,
            publicToken: result?.data?.access_token,
            itemId: result?.data?.item_id,
          }));
          setIsBankLoading(false);
          Toaster.success(result?.data?.message);
        }
      } catch (error) {
        setIsBankLoading(false);
        console.error(error);
      }
    }
  };

  const handlePlaidExit = (error: any, metadata: any) => {
    // alert('exit');
  };

  const handleAddMoreBanks = () => {
    setIsModalVisible(false);
  };

  const handleBankSelection = (event: React.ChangeEvent<HTMLInputElement>, bank: any) => {
    setSelectedBank((prev) => {
      return {
        ...prev,
        bankId: bank?.bankId,
        bankName: bank?.bankName,
        lastFourDigits: bank?.lastFourDigits,
        bankType: bank?.bankType,
        loading: false,
      };
    });
  };

  const handleProceed = async () => {
    if (selectedBank) {
      try {
        setIsModalVisible(false);
        setIsProceedModalVisible(true);
      } catch (error) {
        Toaster.error('An error occurred. Please try again.');
      }
    }
  };
  const handleProceedPayment = async () => {
    if (!selectedBank) {
      Toaster.error('Please select a bank before proceeding.');
      return;
    }

    try {
      setSelectedBank((prev) => (prev ? { ...prev, loading: true } : null));

      const creditLineIds = selectedCreditLine.map((item: any) => ({
        creditLineInvoiceId: item.creditLineInvoiceId,
      }));

      const payload = {
        BankId: selectedBank.bankId,
        Amount: '0.03', // Ensure Amount is a string
        // Amount: dueAmount.toString(),
        AccountId: login?.account?.id,
        creditLineInvoicePaids: creditLineIds.length > 0 ? creditLineIds : undefined, // Exclude if empty
      };

      creditLineIds.forEach((item: any) => {
        dispatch(removeSelectedCreditLineData(item.creditLineInvoiceId));
      });

      const result = await axiosInstanceB2B.post(`${Urls.ACHPaymentDebit}`, payload);
      if (result?.status === 200) {
        setTimeout(async () => {
          await getCreditLine();
          await getCreditLineInvoiceSummary();
          setPaymentLoading(false);
          Toaster.success(result?.data?.message || 'Payment successful!');
        }, 120000);
        setIsProceedModalVisible(false);
        setSelectedBank(null);
        setPaymentLoading(true);
        setTimeout(() => {
          Toaster.info('Please wait for a minute we are processing your payment!');
        }, 1500);
      } else {
        setPaymentLoading(false);
        throw new Error('Payment failed. Please try again.');
      }
    } catch (error) {
      console.error('Payment Error:', error);
      setSelectedBank((prev) => (prev ? { ...prev, loading: false } : null));
      setPaymentLoading(false);
      // Toaster.error(error?.message || "An error occurred. Please try again.");
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedBank(null);
  };
  const handleProceedModalClose = () => {
    setIsProceedModalVisible(false);
  };
  async function getCreditLine() {
    try {
      setLoading(true);
      const res = await axiosInstanceB2B.get(
        `/identity/creditline/ByAccountId/${login?.account?.id}`,
      );
      const result = await axiosInstanceB2B.get(
        `/bookings/CreditLineInvoices?orderBy=paidDate&sortOrder=desc&page=1&results=10`,
      );
      if (res?.status === 200) {
        setCreditLine(res?.data);
        const calculatedProgress = Math.min(
          100,
          Math.max(0, (res?.data?.currentAmount / res?.data?.originalAmount) * 100),
        );
        setProgress(calculatedProgress);
      }
      if (result?.status === 200) {
        const { items } = result?.data;
        dispatch(setCreditLineData(items));
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  }
  async function getCreditLineInvoiceSummary() {
    try {
      const result = await axiosInstanceB2B.get(
        `${Urls.getCreditLineInvoiceSummary}/${login?.account?.id}`,
      );
      if (result?.status === 200) {
        const { data } = result;
        dispatch(
          setCreditLineSummary({
            totalPending: formatAmount(data?.totalPending),
            dueDate: data?.dueDate,
          }),
        );
      }
    } catch (error) {
      setLoading(false);
    }
  }
  useEffect(() => {
    getCreditLineInvoiceSummary();
    getBankAccountList();
    getCreditLine();
    getPlaidACHPaymentList();
  }, []);

  const GetAllCreditLineColumns = (): any[] => {
    const columns = useMyCreditLineColumnMenu();
    return columns.map((c) => {
      const column = { ...c };
      return column;
    });
  };

  const preprocessDataSource = (creditLineInvoices: any) => {
    return creditLineInvoices?.map((item: any) => ({
      ...item,
      amount: parseFloat(item?.amount?.toFixed(2)).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    }));
  };
  const columns = GetAllCreditLineColumns();
  const dataSource = preprocessDataSource(creditLineInvoices);
  const totalOrderCount = dataSource?.length;

  const handleOnFiltersChange = (updatedFilters: any) => {
    setFilters(updatedFilters);
  };

  const handleOnRefresh = () => {};

  const handleOnTableChange = (pagination: any, sorter: any, filter: any) => {
    let paidDate = null;
    if (Array.isArray(sorter?.paidDate) && sorter?.paidDate[0] === null) {
      setFilters((prev) => {
        return {
          ...prev,
          paidDate: {
            value: null,
          },
        };
      });
    } else if (Array.isArray(sorter?.paidDate) && sorter?.paidDate[0]) {
      const extractedDate = sorter.paidDate[0].split('~')[0];
      const [year, month, day] = extractedDate.split('/'); // Split the YYYY/MM/DD format
      paidDate = `${month}/${day}/${year}`; // Format to DD/MM/YYYY
      setFilters((prev) => {
        return {
          ...prev,
          paidDate: {
            value: sorter.paidDate[0].split('~')[0],
          },
        };
      });
    }
    setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
    handleApplyFilters(filter, paidDate);
  };

  const handleOnRowClick = (row: any) => {
    // setRecord(row);
    // setShowMarkAsDetailModal(true);
  };

  const handleOnResetFilters = () => {
    setFilters({
      bookingInternalId: { value: null },
      bookingStatusEnum: { value: null },
      customerLocator: { value: null },
      paidDate: { value: null },
    });
    fetchFilteredData(pagination.current, pagination.pageSize, {}, {});
  };

  const fetchFilteredData = async (
    currentPage: number,
    pageSize: number,
    sorter: any,
    paidDate: any,
  ) => {
    try {
      const sortOrder =
        sorter?.order === 'ascend' ? 'asc' : sorter?.order === 'descend' ? 'desc' : '';
      const params = {
        orderBy: sorter.field || '',
        sortOrder: sortOrder || '',
        page: currentPage || 1,
        results: pageSize || 10,
      };
      // if (paidDate) {
      //   (params.fromPaidDate = paidDate), (params.toPaidDate = paidDate);
      // }

      const result = await axiosInstanceB2B.get('/bookings/CreditLineInvoices', {
        params,
      });
      const data = result.data;
      // setCreditLineInvoices(data.items || []);
      const { items } = data;
      dispatch(setCreditLineData(items || []));
      setPagination({
        current: data.currentPage || currentPage,
        pageSize: data.resultsPerPage || pageSize,
      });
    } catch (error) {
      console.error('Error fetching filtered data:', error);
    }
  };

  const handleApplyFilters = (filters: any, paidDate: any) => {
    fetchFilteredData(pagination.current, pagination.pageSize, filters, paidDate);
  };

  const handleAction = (key: string) => {
    // Add your specific logic for each action here
  };
  const menuItems = [
    {
      key: 'option1',
      label: 'option 1',
      // action: () => handleAction('viewDetails'),
    },
    {
      key: 'option2',
      label: 'option 2',
      // // action: () => handleAction('exportPDF'),
      // disabled: !record?.urlPdf, 
    },
    { key: 'option3', label: 'option 3' },
    {
      key: 'option4',
      label: 'option 4',
      // action: () => handleAction('exportAllPDF'),
    },
  ];
  const menu = (
    <Menu>
      {menuItems.map(({ key, label }) => (
        <Menu.Item key={key} onClick={() => handleAction(key)}>
          {label}
        </Menu.Item>
      ))}
    </Menu>
  );

  const exportToExcel = async () => {
    try {
      setExportLoading(true);
      const response = await axiosInstanceB2B.get(`${Urls.ExportCreditLine}`, { responseType: 'blob' }); 
      if (response?.data) { 
        let blobData = response.data;
        const now = new Date().toISOString().split('T')[0];
        const fileName = `${'billings_export'}_${now}.${FILE_TYPE.EXCEL}`;
        const mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        blobData = new Blob([response.data], { type: mimeType });
        const downloadUrl = window.URL.createObjectURL(blobData);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        Toaster.success(`${intl.formatMessage({ id: 'file.downloaded' })}`);
        setExportLoading(false);
      }
      else {
        Toaster.info(`${intl.formatMessage({ id: 'no.data.received' })}`);
        setExportLoading(false);
      }
    }
    catch (error) {
      setExportLoading(false);
      console.error('Error exporting to Excel:', error);
    }
  }

  return (
    <section className="pt-3">
      <div className="container">
        <Row gutter={[30, 2]}>
          <SideNav />
          <Col lg={16} xl={18}>
            <div className="card shadow-sm border mb-5">
              <div className="card-header border-bottom">
                <h4 className="card-header-title">
                  <i className="bi bi-credit-card mx-2"></i><FormattedMessage id="billing" />
                </h4>
              </div>
              {!paymentLoading ? (
                <div className="dashboardBox px-4 pt-4 pb-3">
                  <Row gutter={[15, 15]}>
                    <Col xl={6} sm={12} xs={24}>
                      <div className="dashboardBox_item d-flex align-items-start h-100">
                        <div className="dashboardBox_item_content d-flex align-items-center justify-content-between flex-column h-100 w-100">
                          <div className="dashboardBox_item_top w-100">
                            <div className="dashboardBox_price">${totalPending}</div>
                            {dueDate && (
                              <div className="dashboardBox_text text-success">
                                <FormattedMessage id="payment.due" /> : <span>{dueDate}</span>
                              </div>
                            )}
                          </div>
                          <div className="dashboardBox_item-bottom w-100">
                            <BlueButton
                              block
                              size="large"
                              type="primary"
                              htmlType="submit"
                              className="btn d-inline-block btn-primary commonBlueBtn blue-button"
                              onClick={handleCreatePlaidToken}
                              disabled={plaidState?.isLoading || totalPending < 1}
                            >
                              {plaidState?.isLoading ? (
                                <Spin
                                  spinning={plaidState?.isLoading}
                                  className="d-flex align-items-center justify-content-center"
                                  style={{ width: '104px' }}
                                ></Spin>
                              ) : bankName?.length > 0 ? (
                                <FormattedMessage id="make.payments" />
                              ) : (
                                <FormattedMessage id="connect.payment" />
                              )}
                            </BlueButton>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col xl={6} sm={12} xs={24}>
                      <div className="dashboardBox_item w-100 h-100">
                        <div className="dashboardBox_item_content d-flex align-items-center justify-content-between flex-column h-100">
                          {creditLine?.currentAmount ? (
                            <div className="dashboardBox_item_top w-100">
                              <div className="dashboardBox_price">
                                $ {creditLine?.currentAmount.toFixed(2)}
                              </div>
                              <div>
                                <div className="dashboardBox_text fw-bold mb-1">
                                  <FormattedMessage id="credit.available" />
                                </div>
                                <div className="progress-bar">
                                  <span
                                    className="progress-bar-fill"
                                    style={{ width: `${progress}%` }}
                                  ></span>
                                </div>
                              </div>
                              <div className="dashboardBox_limit text-end">
                                <FormattedMessage id="credit.limit" />
                              </div>
                            </div>
                          ) : (
                            <Spin
                              spinning={loading}
                              className="h-100 w-100 align-items-center d-flex justify-content-center"
                            ></Spin>
                          )}
                        </div>
                      </div>
                    </Col>
                    <Col xl={6} sm={12} xs={24}>
                      <div className="dashboardBox_item w-100 h-100">
                        <div className="dashboardBox_item_content d-flex align-items-center justify-content-between flex-column h-100">
                          {creditLine?.currentAmount ? (
                            <div className="dashboardBox_item_top w-100">
                              <div className="dashboardBox_text fw-bold mb-1">
                                <FormattedMessage id="credit.balance" />
                                  <em className="ms-1 bi bi-info-circle-fill"></em>
                                </div>
                              <div className="dashboardBox_price">
                                $ {creditLine?.currentAmount.toFixed(2)}
                              </div>
                              <div className="dashboardBox_limit text-start">
                                <FormattedMessage id="credit.limit" /> : $95000
                              </div>
                            </div>
                          ) : (
                            <Spin
                              spinning={loading}
                              className="h-100 w-100 align-items-center d-flex justify-content-center"
                            ></Spin>
                          )}
                        </div>
                      </div>
                    </Col>
                    {/* <Col xl={6} sm={12} xs={24}>
                      <div className="dashboardBox_item dashboardBox_item-blueFill w-100 h-100">
                        <div className="dashboardBox_item_top w-100 h-100 d-flex align-items-start flex-column justify-content-between">
                          <div className="dashboardBox_item_action d-flex align-items-center justify-content-between mb-1 w-100">
                            <img src="./visa.svg" width={32} alt="" />
                            <Dropdown overlay={menu} trigger={['click']} placement="topRight">
                              <span className="dropDown" style={{ cursor: 'pointer' }}>
                                <em className="bi bi-three-dots-vertical" />
                              </span>
                            </Dropdown>
                          </div>
                          <div className="dashboardBox_item_payAuto mt-2">
                            <div className="dashboardBox_item_pay fw-semibold text-start">
                              <FormattedMessage id="set.up.autopay" />
                            </div>
                            <div className="dashboardBox_price text-white">$889.95</div>
                          </div>
                          <div className="dashboardBox_item_cardNumber mt-2">
                            **** **** **** 1234
                          </div>
                          <div className="dashboardBox_item_date w-100 d-flex align-items-center justify-content-between">
                            <div className="dashboardBox_item_date_value">
                              <FormattedMessage id="valid.thru" />: <span>12/24</span>
                            </div>
                            <div className="dashboardBox_item_date_value">
                              <FormattedMessage id="cvv" />: <span>***</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col> */}
                  </Row>
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-center">
                    <Loading size={LoadingSise.Normal} className={'margin-top-30'} />
                  </div>
                </>
              )}
              
              <div className="commonTable">
                <ul className="nav nav-pills-shadow nav-responsive">
                  <li className="nav-item">
                    <a
                      className={`nav-link mb-0 me-sm-2 ${
                        activeTab === 'invoices' && MEMBER_STATUS.ACTIVE
                      }`}
                      onClick={() => setActiveTab('invoices')}
                    >
                      <FormattedMessage id="invoices" />
                    </a>
                  </li>
                  <li className="nav-item">
                    <a
                      className={`nav-link mb-0 me-sm-2 ${
                        activeTab === 'payments' && MEMBER_STATUS.ACTIVE
                      }`}
                      onClick={() => setActiveTab('payments')}
                    >
                      <FormattedMessage id="payments" />
                    </a>
                  </li>
                  {activeTab === 'invoices' && (
                    <div className="ms-auto">
                      <li className="nav-item">
                        <BlueButton type="primary" className="btn btn-primary mw-185 h-38" onClick={exportToExcel}>
                          {!exportLoading && <i className="bi bi-save mx-2"></i>}  
                          {exportLoading ? (<Spin spinning={exportLoading} className='mx-2'/>) : (<FormattedMessage id="export.excel" />)}
                        </BlueButton>
                      </li>
                    </div>
                  )}
                </ul>

                <hr style={{ width: '-webkit-fill-available' }} />

                {activeTab === 'invoices' ? (
                  <div className="searchTable">
                    {!paymentLoading ? (
                      <SearchTable
                        key={'active'}
                        title={''}
                        loading={loading}
                        allColumns={[...columns]}
                        dataSource={dataSource}
                        pagination={pagination}
                        filters={filters}
                        totalOrderCount={totalOrderCount}
                        rowKey={(row: any) => {
                          return row.key ? `${row.key}` : `${row.creditLineInvoiceId}`;
                        }}
                        isFilterApplied={
                          !!Object.values(filters).some((filter) => filter.value !== null)
                        }
                        onFiltersChange={handleOnFiltersChange}
                        onTableChange={handleOnTableChange}
                        onRowClick={handleOnRowClick}
                        onResetFilters={handleOnResetFilters}
                        onApplyFilters={handleApplyFilters}
                        onRefresh={handleOnRefresh}
                        hideRefresh={true}
                      ></SearchTable>
                    ) : (
                      <>
                        <div className="d-flex justify-content-center">
                          <Loading size={LoadingSise.Normal} className={'margin-top-30'} />
                        </div>
                      </>
                    )}

                    <Modal
                      title="Plaid"
                      open={isModalVisible}
                      onCancel={handleModalClose}
                      footer={null}
                      width={modalWidth}
                      className="plaidModal"
                    >
                      <CompanyPlaidComponent
                        plaidState={plaidState}
                        bankName={bankName}
                        selectedBank={selectedBank}
                        handlePlaidExit={handlePlaidExit}
                        handlePlaidSuccess={handlePlaidSuccess}
                        handleAddMoreBanks={handleAddMoreBanks}
                        handleProceed={handleProceed}
                        handleBankSelection={handleBankSelection}
                        isBankLoading={isBankloading}
                        totalPending={totalPending}
                      />
                    </Modal>

                    <>
                      <Modal
                        title="Plaid Proceed Payment"
                        open={isProceedModalVisible}
                        onCancel={handleProceedModalClose}
                        footer={null}
                        width={modalWidth}
                      >
                        <div className="container p-0">
                          <div className="card p-0">
                            <h5 className="mb-3"><FormattedMessage id="bank.details" /></h5>
                            <ul className="list-group">
                              <li className="list-group-item d-flex justify-content-between align-items-center">
                                <strong><FormattedMessage id="bank.name" />:</strong>
                                <span>{selectedBank?.bankName}</span>
                              </li>
                              <li className="list-group-item d-flex justify-content-between align-items-center">
                                <strong><FormattedMessage id="last.four.digits" />:</strong>
                                <span>{selectedBank?.lastFourDigits}</span>
                              </li>
                              <li className="list-group-item d-flex justify-content-between align-items-center">
                                <strong><FormattedMessage id="account.type" />:</strong>
                                <span>{selectedBank?.bankType}</span>
                              </li>
                              <li className="list-group-item d-flex justify-content-between align-items-center">
                                <strong><FormattedMessage id="amount" />:</strong>
                                <span>${totalPending}</span>
                              </li>
                            </ul>
                          </div>
                        </div>

                        <div className="d-flex justify-content-end gap-3 mt-3">
                          <WhiteButton onClick={handleProceedModalClose}>
                            <FormattedMessage id="cancel.button" />
                          </WhiteButton>
                          <BlueButton
                            block
                            size="large"
                            type="primary"
                            className="btn btn-primary commonBlueBtnRounded-lg"
                            onClick={handleProceedPayment}
                            disabled={!selectedBank || selectedBank?.loading}
                          >
                            {selectedBank?.loading ? (
                              <Spin
                                spinning={selectedBank?.loading}
                                style={{ minWidth: '80px' }}
                                className="h-100 w-100 align-items-center d-flex justify-content-center"
                              ></Spin>
                            ) : (
                              <FormattedMessage id="total.pay.now" />
                            )}
                          </BlueButton>
                        </div>
                      </Modal>
                    </>
                  </div>
                ) : (
                  <>
                    <div className="searchTable">
                      <CompanyCreditLinePaymentsList
                        paymentList={paymentList}
                        paymentListLoading={paymentListLoading}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </section>
  );
}

const CompanyCreditLine = CompanyCreditLineComponent;
export default CompanyCreditLine;
